import type { LicenseEntitlementNumericKey, LicenseLimit, LicenseScope } from '#layers/license/public';
import { useOrganizationEntitlement } from '#layers/access/composables/use-organization-entitlement';
import { canManageOrganizationLicense } from '#layers/access/constants/organization-entitlements';
import { LicenseEntitlementLimitDialog, useLicenseStore, useSystemLicenseStore } from '#layers/license/public';
import { showConfirmationDialog } from '#layers/ui/public';

interface LicenseEntitlementLimitOptions {
	scope?: LicenseScope;
}

/**
 * Guards create actions for numeric entitlements against the license limit for the chosen scope.
 */
export function useLicenseEntitlementLimit(options: LicenseEntitlementLimitOptions = {}) {
	const { scope } = options;
	const license = scope === 'system' ? useSystemLicenseStore() : useLicenseStore();
	const { can: canOrganization } = useOrganizationEntitlement();

	async function resolveLimit(key: LicenseEntitlementNumericKey, allowRecent = false): Promise<LicenseLimit | null> {
		if (allowRecent) await license.ensureFresh();
		else await license.refresh();

		return license.isLoaded ? license.getEntitlementLimit(key) : null;
	}

	async function guardCreation(key: LicenseEntitlementNumericKey): Promise<boolean> {
		const limit = await resolveLimit(key);
		const status = limit?.status ?? 'allow';
		if (status === 'allow') return true;

		const confirmed = await showConfirmationDialog({
			component: LicenseEntitlementLimitDialog,
			props: {
				entitlementKey: key,
				status,
				// Show the same allowance the badge renders so the dialog matches it.
				allowance: limit?.allowance ?? 0,
				canManageLicense: canOrganization(canManageOrganizationLicense),
				scope,
			},
		});

		// A hard block never proceeds; the overlay result may be a truthy payload, so coerce it.
		return status === 'warn' && Boolean(confirmed);
	}

	async function isCreationBlocked(key: LicenseEntitlementNumericKey): Promise<boolean> {
		const limit = await resolveLimit(key, true);
		return limit?.status === 'block';
	}

	return { guardCreation, isCreationBlocked };
}
